import { useState } from 'react';
import PropTypes from 'prop-types';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { App as AntdApp, ConfigProvider, Empty } from 'antd';
import enUS from 'antd/locale/en_US';
import trTR from 'antd/locale/tr_TR';
import i18n from '../i18n';
import { SettingsProvider } from '../Context/SettingsContext';
import { componentConfig, themeConfig } from '../theme';

// i18next language code → antd locale pack. Add entries as you ship languages.
const antdLocales = {
  en: enUS,
  tr: trTR,
};

function ThemeProvider({ children }) {
  // Inside I18nextProvider, so the tree re-renders when the language changes.
  const { t, i18n: i18nInstance } = useTranslation();
  const language = (i18nInstance.resolvedLanguage || 'en').split('-')[0];

  return (
    <ConfigProvider
      theme={themeConfig}
      locale={antdLocales[language] ?? enUS}
      renderEmpty={() => (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('common:noData')} />
      )}
      {...componentConfig}
    >
      {/* Lets message / notification / Modal.confirm inherit the theme via App.useApp() */}
      <AntdApp>{children}</AntdApp>
    </ConfigProvider>
  );
}

ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default function Providers({ children }) {
  // Lazy init: one client for the app's lifetime instead of a new cache every render.
  const [queryClient] = useState(() => new QueryClient());

  return (
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <SettingsProvider>
          <ThemeProvider>{children}</ThemeProvider>
        </SettingsProvider>
      </QueryClientProvider>
    </I18nextProvider>
  );
}

Providers.propTypes = {
  children: PropTypes.node.isRequired,
};
